import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type {
  BidLimitCell,
  BidLimitSegmentMeta,
  BlockMandatoryPolicy,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'

import { CardAccordion } from '~/pages/AdminBidLimits/components/CardAccordion'
import { EditingBadge } from '~/pages/AdminBidLimits/components/EditingBadge'
import type {
  BidLimitValue,
  MatrixDraft,
} from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import type { CellKey } from '~/pages/AdminBidLimits/models/cellKey'
import { BlockMandatoryToggleCard } from '~/pages/AdminBidLimits/tabs/SpotBlockTab/BlockMandatoryToggleCard'
import { BidLimitMatrixCard } from '~/pages/AdminBidLimits/tabs/SpotNormalTab/BidLimitMatrixCard'

type Props = {
  cells: BidLimitCell[]
  blockMandatoryPolicy: BlockMandatoryPolicy | undefined
  segmentMeta: BidLimitSegmentMeta | undefined
  draft: MatrixDraft
  saved: MatrixDraft
  /** マトリクスの編集モード (page-level の `useBidLimitMatrixDraft` が持つ) */
  isEditing: boolean
  onStartEdit: () => void
  onSetCell: (key: CellKey, value: BidLimitValue | undefined) => void
  onReset: () => void
  onCommit: (newDraft: MatrixDraft) => void
  /**
   * workflow-back-bar-readonly AC-17: ワークフローケースが完了済み/削除済みのとき true。
   * true のとき必須化トグル・マトリクス保存/リセットを disabled にする。
   */
  isReadonly?: boolean
}

export const SpotBlockTab = ({
  cells: _cells,
  blockMandatoryPolicy,
  segmentMeta,
  draft,
  saved,
  isEditing,
  onStartEdit,
  onSetCell,
  onReset,
  onCommit,
  isReadonly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const enabled = blockMandatoryPolicy?.enabled ?? false

  return (
    <Box>
      {/* Card: 必須化 toggle */}
      {/* 色なしの border ショートハンドは `currentColor` で黒く出る。
          他のカードと同じ Border.lowEmphasis / 白地 / 角丸 lg に揃える。 */}
      <Box
        marginBottom={4}
        padding={4}
        borderWidth={1}
        borderColor="Border.lowEmphasis"
        borderRadius="lg"
        backgroundColor="Surface.primary"
      >
        <BlockMandatoryToggleCard
          blockDraft={draft}
          isReadonly={isReadonly}
          policy={blockMandatoryPolicy}
        />
      </Box>

      {/* カードの器・開閉標は `CardAccordion` に集約した。 8080 `.ecl-accordion`
          (bid-limits.html の各カードも同 class) 準拠で、 2026-08-31 の
          アコーディオン基準統一 (承認フロー ProcessAccordion を基準とする
          デザイナー判断) に従う。 */}
      {/* Card: ブロック入札専用 matrix (必須化 ON 時のみ表示) */}
      {enabled && (
        <CardAccordion
          badge={isEditing ? <EditingBadge /> : undefined}
          title={t('page:adminBidLimits.blockMatrix.title')}
        >
          <BidLimitMatrixCard
            draft={draft}
            isEditing={isEditing}
            isReadonly={isReadonly}
            onCommit={onCommit}
            onReset={onReset}
            onSetCell={onSetCell}
            onStartEdit={onStartEdit}
            saved={saved}
            segment={BidSegment.SPOT_BLOCK}
            segmentMeta={segmentMeta}
          />
        </CardAccordion>
      )}
    </Box>
  )
}
